import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from 'app/shared/providers/AuthProvider';
import { useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import queryString from 'query-string';

// Components
import {
  U21Button,
  U21Section,
  U21Form,
  U21FormField,
  U21FormFieldTypes,
  U21Spacer,
  U21Typography,
} from 'app/shared/u21-ui/components';
import ToastMessages from 'app/shared/toasts/components/ToastMessages';
import LogoutModal from 'app/shared/components/LogoutModal';

// Selectors
import { selectAuthEmailSent } from 'app/modules/session/selectors';

// Thunks
import {
  initAuthEmailSent,
  sendAuthEmailThunk,
} from 'app/modules/session/sliceSession';

// Utils
import loginInPattern from 'app/images/log-in-pattern.png';
import emailCloud from 'app/images/email-cloud.png';
import { IconMail, IconBrandGoogle } from '@u21/tabler-icons';
import isEmail from 'validator/lib/isEmail';

// Used for error messages
import { sendErrorToast } from 'app/shared/toasts/actions';
import { LocalStorageKeys } from 'app/shared/constants/localStorage';
import { useLocalStorage } from 'app/shared/hooks/useLocalStorage';
import fraudFightersLogo from 'app/images/logos/FraudFighters.png';
import { selectFraudFighterLogoEnabled } from 'app/shared/featureFlags/selectors';
import { Unit21Logo } from 'app/shared/components/logos/Unit21Logo';

const Login = () => {
  const fraudFighterLogoEnabled = useSelector(selectFraudFighterLogoEnabled);

  const [email, setEmail] = useState<string>('');
  const [showLogoutModal, setShowLogoutModal] = useState<boolean>(false);
  const dispatch = useDispatch();
  const [auth0ErrorMessage, setAuth0ErrorMessage] = useLocalStorage<string>(
    LocalStorageKeys.AUTH0_LOGIN_ERROR,
  );
  const { authClient, setAuthClient, auth0 } = useAuth();
  const authEmailSent = useSelector(selectAuthEmailSent);
  const { search } = useLocation();
  const history = useHistory();

  useEffect(() => {
    if (authClient.provider === 'auth0') {
      authClient.authorize();
    }
  }, [authClient]);

  useEffect(() => {
    if (search) {
      const { logout }: { logout?: boolean } = queryString.parse(search);
      setShowLogoutModal(Boolean(logout));
    }
  }, [search]);

  useEffect(() => {
    if (auth0ErrorMessage) {
      dispatch(sendErrorToast(auth0ErrorMessage));
      setAuth0ErrorMessage('');
    }
  }, [auth0ErrorMessage, setAuth0ErrorMessage, dispatch]);

  const reEnterEmail = () => {
    dispatch(initAuthEmailSent());
  };

  const updateClientAuth0 = () => {
    auth0.setConnectionType(undefined);
    setAuthClient(auth0);
  };

  const dismissLogoutModal = () => {
    setShowLogoutModal(false);
    history.replace('/agent-login');
  };

  return (
    <Container>
      <LogoutModal open={showLogoutModal} dismiss={dismissLogoutModal} />
      <ToastMessages />
      <StyledU21Section>
        <>
          {!authEmailSent ? (
            <StyledU21Spacer spacing={4}>
              <StyledUnit21Logo />
              <StyledU21Form
                onSubmit={(values: { email: string }) => {
                  setEmail(values.email);
                  dispatch(sendAuthEmailThunk({ email: values.email }));
                }}
              >
                <U21FormField<string | undefined>
                  fieldProps={{
                    clearable: false,
                    label: 'Your work email',
                  }}
                  name="email"
                  type={U21FormFieldTypes.TEXT}
                  validate={(value = '') =>
                    isEmail(value) ? undefined : 'Please enter a valid email.'
                  }
                />
                <ButtonContainer>
                  <U21Button
                    color="primary"
                    size="large"
                    startIcon={<IconMail size={24} />}
                    type="submit"
                    variant="contained"
                  >
                    Send me a magic link
                  </U21Button>
                </ButtonContainer>
              </StyledU21Form>
              <U21Button
                onClick={() => {
                  auth0.setConnectionType('google-oauth2');
                  setAuthClient(auth0);
                }}
                startIcon={<IconBrandGoogle size={24} />}
                size="large"
                color="primary"
              >
                Continue with Google
              </U21Button>
              <HiddenLink id="cypress-login" onClick={updateClientAuth0}>
                Continue with Auth0
              </HiddenLink>
              <StyledU21Typography variant="body2">
                Or use SAML to login if you have set it up.
              </StyledU21Typography>
            </StyledU21Spacer>
          ) : (
            <StyledU21Spacer spacing="16px">
              <img alt="Email" src={emailCloud} />
              <StyledU21Typography variant="h4">
                Check your email
              </StyledU21Typography>
              <StyledU21Typography variant="body2">
                If your email address <b>{email}</b> is registered with Unit21,
                you&apos;ll receive a magic link to login!
              </StyledU21Typography>
              <StyledU21Typography variant="caption" color="text.secondary">
                Can&apos;t see the email? Please check your spam folder.
                <br />
                Wrong Email?{' '}
                <U21Button
                  color="primary"
                  onClick={reEnterEmail}
                  variant="text"
                >
                  Please re-enter your address.
                </U21Button>
              </StyledU21Typography>
            </StyledU21Spacer>
          )}
        </>
      </StyledU21Section>
      {fraudFighterLogoEnabled && <FraudFightersLogo src={fraudFightersLogo} />}
    </Container>
  );
};

export default Login;

const Container = styled('div')`
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-image: url(${loginInPattern});
  background-position: center;
  background-size: contain;
`;

const HiddenLink = styled('a')`
  display: none;
`;

const StyledU21Section = styled(U21Section)`
  max-width: 575px;
  width: 100%;
  border-radius: 16px;
`;

const StyledU21Spacer = styled(U21Spacer)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-width: 460px;
  margin: 0 auto;
  padding-top: 30px;
`;

const StyledUnit21Logo = styled(Unit21Logo)`
  width: 250px;
`;

const StyledU21Form = styled(U21Form)`
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

const StyledU21Typography = styled(U21Typography)`
  width: 100%;
  justify-content: center;
  text-align: center;
`;

const FraudFightersLogo = styled.img`
  border-radius: 6px;
  position: absolute;
  bottom: 16px;
  right: 16px;
  height: 10%;
`;
